import { createAuthClient } from 'better-auth/react';
import { criarClienteApi, ErroApiNyroh, type ClienteApi } from '@nyroh/contracts';

import { exigirEnv } from './env';

/**
 * ============================================================================
 * O CANAL DA FÁBRICA COM O SERVIDOR
 * ============================================================================
 *
 * Substitui TRÊS clientes Supabase que viviam neste projeto:
 *
 *   lib/supabase.ts          o banco da fábrica
 *   lib/supabaseExternal.ts  o banco externo de leads
 *   lib/externalSupabase.ts  duplicata do anterior, com a credencial literal,
 *                            sem nenhum importador — código morto que continuava
 *                            vazando chave no repositório
 *
 * Eram três bancos diferentes para o que sempre foi um domínio só. Agora é uma
 * API, e o navegador não tem credencial de banco nenhuma.
 */

export const API_URL = exigirEnv(
  'VITE_API_URL',
  import.meta.env.VITE_API_URL,
  'URL da API Nyroh (ex.: http://localhost:3333 no ambiente local)',
);

export const CDN_ASSETS = exigirEnv(
  'VITE_CDN_BASE_ASSETS',
  import.meta.env.VITE_CDN_BASE_ASSETS,
  'base pública do bucket de imagens',
);

/**
 * Autenticação do operador.
 *
 * Substitui `localStorage.setItem('ditho_admin_auth', 'true')` com a senha
 * comparada como string dentro do bundle — uma sessão que qualquer pessoa
 * forjava pelo console em uma linha, sem saber a senha.
 */
export const auth = createAuthClient({
  baseURL: `${API_URL}/api/auth`,
  fetchOptions: { credentials: 'include' },
});

/** Sessão do cliente final (portal). Token curto, em sessionStorage. */
const CHAVE_TOKEN = 'nyroh:portal';

export function tokenDoPortal(): string | null {
  try {
    return sessionStorage.getItem(CHAVE_TOKEN);
  } catch {
    return null;
  }
}

export function guardarTokenDoPortal(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(CHAVE_TOKEN, token);
    else sessionStorage.removeItem(CHAVE_TOKEN);
  } catch {
    /* armazenamento bloqueado: a sessão simplesmente não persiste */
  }
}

export const api: ClienteApi = criarClienteApi({
  baseUrl: API_URL,
  obterToken: () => tokenDoPortal() ?? undefined,
  comCredenciais: true,
  aoErro: (erro) => {
    if (erro.precisaAutenticar) guardarTokenDoPortal(null);
    console.error(`[api] ${erro.rota ?? '?'} -> ${erro.codigo}: ${erro.message}`);
  },
});

export { ErroApiNyroh };

/**
 * Envia uma imagem direto para o R2, com URL pré-assinada.
 *
 * Substitui `supabase.storage.from('uploads').upload(...)`. O bucket `uploads`
 * era público para ESCRITA anônima: qualquer visitante trocava ou apagava
 * imagem de qualquer cliente. Aqui a escrita exige autorização do servidor.
 */
/**
 * Sobe a imagem e devolve a CHAVE do objeto, alem da URL.
 *
 * A chave e o que o spec guarda (`refImagemSchema`), e nao a URL: e ela que
 * permite trocar o dominio do CDN sem reescrever cem mil specs. Quem so precisa
 * exibir usa `enviarImagem`, que devolve a URL pronta.
 */
export async function enviarImagemComChave(
  arquivo: File,
  destino: { siteId?: string; clienteId?: string } = {},
): Promise<{ chaveR2: string; url: string }> {
  const assinatura = await api.chamar('assets.assinarUpload', {
    corpo: {
      nomeArquivo: arquivo.name,
      mime: arquivo.type,
      bytes: arquivo.size,
      ...destino,
    },
  });

  const resposta = await fetch(assinatura.url, {
    method: 'PUT',
    headers: { 'content-type': arquivo.type },
    body: arquivo,
  });
  if (!resposta.ok) {
    throw new Error(
      `Falha ao enviar a imagem (${resposta.status}). ` +
        'No ambiente local, confira se o MinIO está no ar.',
    );
  }

  const registrado = await api.chamar('assets.registrar', {
    corpo: {
      chaveR2: assinatura.chaveR2,
      mime: arquivo.type,
      bytes: arquivo.size,
      ...destino,
    },
  });
  return { chaveR2: assinatura.chaveR2, url: registrado.url };
}

/** A mesma coisa, devolvendo so a URL. Mantida para quem ja usava. */
export async function enviarImagem(
  arquivo: File,
  destino: { siteId?: string; clienteId?: string } = {},
): Promise<string> {
  return (await enviarImagemComChave(arquivo, destino)).url;
}
